import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as HamburguerMenu } from "@assets/Icons/IconHamburguerMenu.svg";
import { ReactComponent as LeftIcon } from "@assets/Icons/IconLeft.svg";
import { useAppDispatch } from "../../../shared/redux/hooks";
import { getBackRoute, getPageTitle } from "./helpers/getPageTitle";
import RenderTabsByRoute, { InlineRouteTabs } from "./helpers/getRenderTabsByRoute";
import { usePageHeaderActionsSlot } from "../PageHeaderActions";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { toggleMobileMenu } from "@redux/slices/sideMenuSlice";
import GenericButton from "@ui-components/Button/GenericButton";
import { SideNavTitleSlotTarget, routeUsesTitleSlot } from "./SideNavTitleSlot";
import HeaderAccountControl from "./components/HeaderAccountControl";
import HeaderApiControl from "./components/HeaderApiControl";
import ViewsBar from "../tabs/ViewsBar";
import { useViews } from "../tabs/ViewsProvider";
import { isViewablePath } from "../tabs/viewsModel";

/**
 * Top bar: page title + Back button + route tabs/filters, the mobile hamburger,
 * and — at the end of the title row — the identity and settings controls
 * (CTX-636, CTX-637).
 *
 * Those two sit on the title row rather than in a row of their own so the
 * header keeps the height it has had since CTX-115 emptied its chrome row. The
 * partner/customer selector stays in the rail's top chrome; only identity and
 * settings moved up here.
 */
export default function SideNav() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const backRoute = getBackRoute(location.pathname, location.search);
  const headerActions = usePageHeaderActionsSlot();
  const usesTitleSlot = routeUsesTitleSlot(location.pathname);
  // Open views (project 13D) live in this row. While one is active it is the heading, so
  // the plain title steps aside; outside the views (Settings, Admin Center) the title
  // stays and the views sit beside it.
  const { views, activeId } = useViews();
  const hasViews = views.length > 0;
  const viewIsHeading = hasViews && activeId !== null && isViewablePath(location.pathname);

  // Mobile detection — only update when crossing the breakpoint.
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(() => width <= 1024);
  const prevIsMobileRef = useRef(isMobile);
  useEffect(() => {
    const newIsMobile = width <= 1024;
    if (prevIsMobileRef.current !== newIsMobile) {
      prevIsMobileRef.current = newIsMobile;
      setIsMobile(newIsMobile);
    }
  }, [width]);

  const handleHamburgerClick = () => {
    dispatch(toggleMobileMenu());
  };

  return (
    <header
      className="
        w-full
        bg-semantic-bg-primary
        text-semantic-text-secondary
        shadow-md
        px-6
        py-2
        flex
        flex-col
        gap-2
      "
    >
      {/* Mobile-only hamburger row. Desktop chrome (partner/customer, profile)
          moved into the sidebar account card, so there's no top-bar row here. */}
      {isMobile && (
        <div
          className="
            flex items-center
            pb-2
            -mx-6 px-6
            border-b border-semantic-border-secondary
          "
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-sm
              border border-semantic-border-secondary
              bg-semantic-bg-secondary"
          >
            <GenericButton
              onClick={handleHamburgerClick}
              isIconOnly={true}
              icon={
                <HamburguerMenu className="h-4 w-4 text-semantic-text-primary" />
              }
              variant="refresh"
              size="md"
              customHeight="40px"
              className="rounded-full"
              aria-label="Toggle menu"
            />
          </div>
        </div>
      )}

      {/* Título principal con botón de regreso si es necesario.
          The row wraps; the controls beside it must not, so they sit in this
          non-wrapping parent rather than inside the wrapping row — sharing it
          took width from the title slot and pushed its content onto a second
          line on the routes that register one (intent I6). */}
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          {backRoute && (
            <GenericButton
              variant="secondary"
              shape="ghost"
              size="sm"
              icon={
                <LeftIcon className="h-5 w-5 text-semantic-text-secondary" />
              }
              onClick={() => navigate(backRoute)}
              aria-label="Go back"
            />
          )}
          {pageTitle && !viewIsHeading && (
            <h2
              className={`
              text-xl
              font-medium
              text-[var(--semantic-text-secondary)]
              ${usesTitleSlot || headerActions ? "whitespace-nowrap" : ""}
            `}
            >
              {pageTitle}
            </h2>
          )}
          {hasViews && <ViewsBar />}
          <InlineRouteTabs />
          {/* Page-owned controls on the title's own line, only for routes that opted in — every
            other screen renders this row exactly as it did before the slot existed. */}
          {usesTitleSlot && (
            <SideNavTitleSlotTarget className="min-w-0 flex-1" />
          )}
          {/* CTX-112 — header actions the active page registered via
            PageHeaderActionsProvider (e.g. Insight Topology V2's compact
            toolbar), rendered beside the title exactly like the title slot. */}
          {headerActions && (
            <div
              className="flex min-w-0 flex-1 flex-wrap items-center gap-3"
              data-testid="page-header-actions"
            >
              {headerActions}
            </div>
          )}
        </div>

        {/* Pinned to the end of the title row for every route. `shrink-0` so a
            long title or a busy toolbar squeezes itself, never this.

            The gear that stood here opened the whole account list, which lives
            in the rail again: two surfaces for the same rows is how they start
            disagreeing, and the rail is where they were asked for. What stays
            is identity, and API beside it — the one destination that was never
            findable inside a menu. */}
        <div className="flex shrink-0 items-center gap-1">
          <HeaderApiControl />
          <HeaderAccountControl />
        </div>
      </div>

      {/* `empty:hidden`: most routes have no sub-tabs, and an empty row still took the
          header's gap — vertical room every screen paid for nothing. */}
      <div className="w-3xl empty:hidden">
        <RenderTabsByRoute />
      </div>
    </header>
  );
}
